import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Calculator,
  ChefHat,
  BookOpen,
  List,
  AlertTriangle,
  Lightbulb,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cuánto tiempo se tarda en hacer un escandallo?',
    a: 'Hacer un escandallo manualmente por primera vez lleva entre 15 y 30 minutos por plato, dependiendo del número de ingredientes. Con práctica, puedes reducirlo a 10 minutos. Con un software como PLEKO, el proceso se reduce a 2-3 minutos porque los precios e ingredientes ya están cargados y las mermas se aplican automáticamente.',
  },
  {
    q: '¿Necesito hacer escandallo de todos los platos?',
    a: 'Sí, es muy recomendable. Si solo escandallos algunos platos, no puedes calcular el food cost global de tu restaurante ni comparar la rentabilidad entre ellos. Lo ideal es escandallar toda la carta, incluyendo postres, entrantes, bebidas elaboradas y menús del día. Empieza por los platos más vendidos y los más caros.',
  },
  {
    q: '¿Cómo calculo la merma si no la conozco?',
    a: 'La forma más precisa es pesar el ingrediente antes y después de limpiarlo/procesarlo. Merma (%) = ((Peso bruto - Peso neto) / Peso bruto) × 100. Si no puedes pesar en el momento, usa tablas de referencia: pescado entero 40-50%, carnes con hueso 20-35%, verduras 10-25%, mariscos con cáscara 35-60%. Puedes consultar nuestra guía completa de mermas en /mermas-cocina.',
  },
  {
    q: '¿Cada cuánto debo actualizar los escandallos?',
    a: 'Cada vez que cambien los precios de compra. Los productos frescos pueden variar un 10-20% en una sola semana. Lo ideal es actualizar los precios cada vez que recibes una factura o albarán del proveedor. Con un software como PLEKO, escaneas el albarán con el móvil y todos los escandallos se actualizan automáticamente en tiempo real.',
  },
  {
    q: '¿Qué hago si mi food cost es mayor al 35%?',
    a: 'Tienes tres opciones principales: 1) Subir el precio de venta (PVP) si el mercado lo permite. 2) Reducir el coste sustituyendo ingredientes caros por alternativas más económicas sin perder calidad. 3) Ajustar las cantidades o el gramaje por ración. También puedes negociar mejores precios con tus proveedores o buscar proveedores alternativos. Consulta nuestra guía de ingeniería de menú para estrategias avanzadas.',
  },
  {
    q: '¿Los escandallos incluyen IVA?',
    a: 'No. Los escandallos trabajan siempre con precios sin IVA (precio neto de compra). El IVA es un impuesto que no forma parte del coste real de producción del plato. Cuando registres los precios de tus ingredientes, asegúrate de usar el precio sin IVA que aparece en el albarán o factura del proveedor.',
  },
  {
    q: '¿Cómo hago un escandallo de una bebida?',
    a: 'El proceso es idéntico al de un plato: lista los ingredientes (licor, mezclador, hielo, guarnición), aplica las cantidades exactas (en ml para líquidos), busca los precios por litro y calcula el coste total. No olvides incluir el coste del hielo (aproximadamente 0,50€/kg), guarniciones (rodaja de limón, aceituna) y el coste de la cristalería desechable si aplica.',
  },
  {
    q: '¿Puedo hacer escandallos para menús completos?',
    a: 'Sí, y es muy recomendable. Para un menú del día o menú degustación, haz el escandallo de cada plato individualmente y después suma los costes de todos los componentes del menú (entrante + principal + postre + bebida + pan). Divide entre el número de comensales si es necesario. Esto te permite fijar el precio del menú con un margen garantizado.',
  },
];

/* ─── Ingredient data for Lubina example ─────────────── */

const lubinaIngredients = [
  { nombre: 'Lubina fresca entera', cantBruta: '1.200 g', merma: '45%', cantNeta: '660 g', precioUd: '18,00 €/kg', coste: '21,60 €' },
  { nombre: 'Patata panadera', cantBruta: '400 g', merma: '15%', cantNeta: '340 g', precioUd: '1,80 €/kg', coste: '0,72 €' },
  { nombre: 'Pimiento verde', cantBruta: '150 g', merma: '15%', cantNeta: '127 g', precioUd: '2,50 €/kg', coste: '0,38 €' },
  { nombre: 'Cebolla', cantBruta: '200 g', merma: '10%', cantNeta: '180 g', precioUd: '1,60 €/kg', coste: '0,32 €' },
  { nombre: 'Tomate cherry', cantBruta: '100 g', merma: '5%', cantNeta: '95 g', precioUd: '4,50 €/kg', coste: '0,45 €' },
  { nombre: 'Ajo (4 dientes)', cantBruta: '20 g', merma: '15%', cantNeta: '17 g', precioUd: '8,00 €/kg', coste: '0,16 €' },
  { nombre: 'Aceite de oliva virgen extra', cantBruta: '80 ml', merma: '0%', cantNeta: '80 ml', precioUd: '9,50 €/L', coste: '0,76 €' },
  { nombre: 'Vino blanco', cantBruta: '50 ml', merma: '0%', cantNeta: '50 ml', precioUd: '6,00 €/L', coste: '0,30 €' },
  { nombre: 'Limón', cantBruta: '1 ud', merma: '0%', cantNeta: '1 ud', precioUd: '0,30 €/ud', coste: '0,30 €' },
  { nombre: 'Perejil fresco', cantBruta: '10 g', merma: '10%', cantNeta: '9 g', precioUd: '12,00 €/kg', coste: '0,12 €' },
  { nombre: 'Sal gruesa', cantBruta: '15 g', merma: '0%', cantNeta: '15 g', precioUd: '0,40 €/kg', coste: '0,01 €' },
  { nombre: 'Pimienta negra', cantBruta: '2 g', merma: '0%', cantNeta: '2 g', precioUd: '25,00 €/kg', coste: '0,05 €' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Cómo Hacer un Escandallo de Cocina Paso a Paso',
  description:
    'Aprende a crear escandallos profesionales para tu restaurante en 7 pasos. Incluye ejemplo completo con Lubina a la Espalda, tabla de mermas y cálculo de food cost.',
  totalTime: 'PT30M',
  estimatedCost: {
    '@type': 'MonetaryAmount',
    currency: 'EUR',
    value: '0',
  },
  supply: [
    { '@type': 'HowToSupply', name: 'Lista de ingredientes con precios actualizados' },
    { '@type': 'HowToSupply', name: 'Tabla de mermas por ingrediente' },
    { '@type': 'HowToSupply', name: 'Báscula de cocina' },
    { '@type': 'HowToSupply', name: 'Calculadora o software de escandallos' },
  ],
  tool: [
    { '@type': 'HowToTool', name: 'Albaranes o facturas de proveedores' },
    { '@type': 'HowToTool', name: 'Hoja de cálculo o software PLEKO' },
  ],
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Elige el plato y define las raciones',
      text: 'Selecciona el plato que vas a escandallar y decide cuántas raciones produce la receta. En nuestro ejemplo: Lubina a la Espalda para 4 raciones.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-1',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Lista todos los ingredientes con cantidades exactas',
      text: 'Anota cada ingrediente de la receta con su cantidad bruta exacta (antes de limpiar o procesar). Incluye TODOS los ingredientes, incluso sal, aceite y especias. Para la Lubina: 1,2 kg lubina entera, 400 g patata, 150 g pimiento verde, 200 g cebolla, etc.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-2',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Busca los precios actualizados de cada ingrediente',
      text: 'Consulta tus albaranes o facturas recientes para obtener el precio actualizado de cada ingrediente. Convierte todos los precios a la misma unidad (€/kg o €/L) para poder hacer los cálculos correctamente.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-3',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Aplica las mermas de cada ingrediente',
      text: 'Calcula la cantidad neta (aprovechable) de cada ingrediente aplicando el porcentaje de merma. Fórmula: Cantidad neta = Cantidad bruta × (1 - merma%). Ejemplo: Lubina entera 1.200 g con 45% merma = 660 g de carne aprovechable.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-4',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Calcula el coste de cada ingrediente',
      text: 'Multiplica la cantidad bruta de compra por el precio unitario. Fórmula: Coste = Cantidad bruta (en kg) × Precio por kg. Ejemplo: 1,2 kg lubina × 18 €/kg = 21,60 €. Suma todos los costes para obtener el coste total del plato.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-5',
    },
    {
      '@type': 'HowToStep',
      position: 6,
      name: 'Calcula el coste por ración y el food cost',
      text: 'Divide el coste total entre el número de raciones para obtener el coste por ración. Después calcula el food cost: Food cost (%) = (Coste ración / PVP) × 100. Ejemplo: 25,17 € / 4 raciones = 6,29 €/ración. Con PVP de 22 €: food cost = 28,6% (verde).',
      url: 'https://pleko.app/como-hacer-escandallo#paso-6',
    },
    {
      '@type': 'HowToStep',
      position: 7,
      name: 'Ajusta precios o ingredientes según el resultado',
      text: 'Evalúa el resultado con el semáforo de food cost: verde (<30%), amarillo (30-35%), rojo (>35%). Si el food cost es demasiado alto, ajusta el PVP, sustituye ingredientes caros o reduce el gramaje. Si es muy bajo, considera mejorar ingredientes.',
      url: 'https://pleko.app/como-hacer-escandallo#paso-7',
    },
  ],
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Cómo Hacer un Escandallo de Cocina Paso a Paso — Guía Completa',
  description:
    'Aprende cómo hacer un escandallo de cocina en 7 pasos con nuestro tutorial completo. Ejemplo real de Lubina a la Espalda con ingredientes, mermas y cálculo de food cost.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: {
    '@type': 'Organization',
    name: 'PLEKO',
    logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' },
  },
  datePublished: '2026-06-17',
  dateModified: '2026-06-17',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Cómo Hacer un Escandallo',
      item: 'https://pleko.app/como-hacer-escandallo',
    },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'que-necesitas', label: '¿Qué necesitas antes de empezar?' },
  { id: 'paso-1', label: 'Paso 1: Elige el plato y define las raciones' },
  { id: 'paso-2', label: 'Paso 2: Lista todos los ingredientes' },
  { id: 'paso-3', label: 'Paso 3: Busca los precios actualizados' },
  { id: 'paso-4', label: 'Paso 4: Aplica las mermas' },
  { id: 'paso-5', label: 'Paso 5: Calcula el coste de cada ingrediente' },
  { id: 'paso-6', label: 'Paso 6: Calcula el coste por ración y food cost' },
  { id: 'paso-7', label: 'Paso 7: Ajusta precios o ingredientes' },
  { id: 'resumen', label: 'Resumen del escandallo completo' },
  { id: 'consejos', label: '5 consejos profesionales' },
  { id: 'herramientas', label: 'Herramientas para hacer escandallos' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function ComoHacerEscandalloPage() {
  return (
    <>
      <Helmet>
        <title>Cómo Hacer un Escandallo Paso a Paso — Tutorial Completo [2026] | PLEKO</title>
        <meta
          name="description"
          content="Aprende cómo hacer un escandallo de cocina en 7 pasos con nuestro tutorial completo. Ejemplo real de Lubina a la Espalda con ingredientes, mermas y cálculo de food cost."
        />
        <meta
          name="keywords"
          content="como hacer un escandallo, como hacer escandallo, como se hace un escandallo, como hacer un escandallo de cocina, escandallo paso a paso, tutorial escandallo, hacer escandallo restaurante, como calcular escandallo paso a paso, escandallo ejemplo lubina"
        />
        <link rel="canonical" href="https://pleko.app/como-hacer-escandallo" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/como-hacer-escandallo" />
        <meta
          property="og:title"
          content="Cómo Hacer un Escandallo Paso a Paso — Tutorial Completo [2026]"
        />
        <meta
          property="og:description"
          content="Tutorial completo para hacer escandallos de cocina en 7 pasos. Ejemplo real de Lubina a la Espalda con precios, mermas y food cost."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Cómo Hacer un Escandallo Paso a Paso [2026] | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Tutorial completo: 7 pasos para hacer un escandallo de cocina con ejemplo real y cálculos detallados."
        />
        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <ChefHat className="w-4 h-4" />
                Tutorial paso a paso — con ejemplo práctico
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Cómo Hacer un <span className="text-blue-500">Escandallo</span> de Cocina Paso a Paso — Guía Completa
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Aprende a crear escandallos profesionales para tu restaurante en 7 pasos. Incluye ejemplo completo con <strong>Lubina a la Espalda</strong>, tabla de mermas y cálculo de food cost.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#paso-1"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200"
                >
                  Empezar el tutorial
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Hacer escandallo automático
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de este tutorial</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: PREREQUISITES ═══════════ */}
            <h2 id="que-necesitas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              ¿Qué necesitas antes de empezar?
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Antes de hacer tu primer escandallo, asegúrate de tener preparados estos cuatro elementos. Sin ellos, el resultado no será fiable:
            </p>
            <div className="space-y-3 mb-6">
              {[
                'Lista completa de ingredientes de la receta con cantidades exactas (en gramos, kg, ml o unidades)',
                'Precios actualizados de cada ingrediente (consulta tus últimos albaranes o facturas)',
                'Número de raciones que produce la receta',
                'Precio de venta estimado (PVP) del plato en tu carta',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Consejo profesional</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Pide albaranes actualizados a tus proveedores antes de empezar. Un escandallo con precios de hace 3 meses es un escandallo que miente. Si quieres automatizar este proceso, <Link to="/ocr-facturas-restaurante" className="text-amber-800 underline hover:text-amber-900">PLEKO escanea albaranes con OCR</Link> y actualiza todos los precios automáticamente.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ THE 7 STEPS ═══════════ */}

            {/* ── PASO 1 ── */}
            <h2 id="paso-1" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">1</span>
                Elige el plato y define las raciones
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El primer paso para hacer un escandallo es elegir el plato que vas a analizar y determinar cuántas raciones produce tu receta. Este dato es fundamental porque el <strong>coste por ración</strong> es lo que realmente necesitas para fijar el precio de venta.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para nuestro ejemplo vamos a utilizar un plato muy popular en la hostelería española: la <strong>Lubina a la Espalda</strong>, una receta para <strong>4 raciones</strong>. Es un plato perfecto para aprender a escandallar porque combina un ingrediente principal caro (la lubina) con mermas significativas, guarniciones variadas y condimentos que muchos hosteleros olvidan incluir.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
              <p className="text-blue-800 font-semibold mb-2">Nuestro ejemplo: Lubina a la Espalda</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Plato</p>
                  <p className="font-bold text-blue-700">Lubina a la Espalda</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Raciones</p>
                  <p className="font-bold text-blue-700">4</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Tipo</p>
                  <p className="font-bold text-blue-700">Segundo plato</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">PVP estimado</p>
                  <p className="font-bold text-blue-700">22,00 €</p>
                </div>
              </div>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              ¿Por qué importa definir las raciones desde el principio? Porque muchas recetas se elaboran en lote (para 4, 6 u 8 personas) y el coste total no te dice nada sobre la rentabilidad. Lo que necesitas saber es cuánto te cuesta <strong>cada plato individual</strong> que sirves al cliente. Si tu receta de lubina cuesta 25 € en ingredientes pero produce 4 raciones, el dato relevante es que cada plato cuesta 6,25 €, no los 25 € totales.
            </p>

            {/* ── PASO 2 ── */}
            <h2 id="paso-2" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">2</span>
                Lista todos los ingredientes con cantidades exactas
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Este es el paso más importante y donde se cometen más errores. Tienes que anotar <strong>absolutamente todos los ingredientes</strong> de la receta con su cantidad bruta (antes de limpiar, pelar o procesar). No omitas nada: el aceite de cocción, la sal, las especias, el limón de decoración. Los costes ocultos están en lo que no mides.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para nuestra <strong>Lubina a la Espalda (4 raciones)</strong>, estos son todos los ingredientes:
            </p>
            <div className="space-y-3 mb-6">
              {[
                { nombre: 'Lubina fresca entera', cantidad: '1,2 kg (1 pieza grande)', nota: 'para abrir a la espalda' },
                { nombre: 'Patata panadera', cantidad: '400 g', nota: 'cortada en rodajas finas' },
                { nombre: 'Pimiento verde', cantidad: '150 g', nota: 'en juliana' },
                { nombre: 'Cebolla', cantidad: '200 g', nota: 'en juliana' },
                { nombre: 'Tomate cherry', cantidad: '100 g', nota: 'cortados por la mitad' },
                { nombre: 'Ajo', cantidad: '20 g (4 dientes)', nota: 'laminado' },
                { nombre: 'Aceite de oliva virgen extra', cantidad: '80 ml', nota: 'para cocción y acabado' },
                { nombre: 'Vino blanco', cantidad: '50 ml', nota: 'para deglasear' },
                { nombre: 'Limón', cantidad: '1 unidad', nota: 'para servir' },
                { nombre: 'Perejil fresco', cantidad: '10 g', nota: 'picado para decoración' },
                { nombre: 'Sal gruesa', cantidad: '15 g', nota: '' },
                { nombre: 'Pimienta negra', cantidad: '2 g', nota: 'recién molida' },
              ].map(({ nombre, cantidad, nota }) => (
                <div key={nombre} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-medium text-slate-800">{nombre}</span>
                    <span className="text-slate-500 ml-2">— {cantidad}</span>
                    {nota && <span className="text-slate-400 ml-1 text-sm">({nota})</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Error frecuente: olvidar ingredientes "invisibles"</p>
                  <p className="text-red-700 text-sm mt-1">
                    El aceite, la sal, las especias y las guarniciones pequeñas parecen insignificantes, pero sumados pueden representar entre un <strong>5% y un 15%</strong> del coste total del plato. En nuestro ejemplo, solo el aceite de oliva ya supone 0,76 €. No incluirlos es inflar artificialmente tu margen.
                  </p>
                </div>
              </div>
            </div>

            {/* ── PASO 3 ── */}
            <h2 id="paso-3" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">3</span>
                Busca los precios actualizados de cada ingrediente
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ahora necesitas el precio de compra actual de cada ingrediente. Las fuentes más fiables son:
            </p>
            <ul className="space-y-2 mb-6">
              {[
                'Albaranes de tus últimas entregas (la fuente más precisa)',
                'Facturas de proveedores del último mes',
                'Tarifas actualizadas que te envía tu distribuidor',
                'Precios de mercados mayoristas como Mercabarna o Mercamadrid',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              <strong>Importante:</strong> convierte todos los precios a la misma unidad base (<strong>€/kg</strong> para sólidos o <strong>€/L</strong> para líquidos) antes de calcular. Si tu proveedor te vende la lubina a 18 €/kg y el perejil a 1,20 € el manojo de 100 g, necesitas convertir el perejil a 12 €/kg para que los cálculos sean homogéneos.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Aquí tienes la tabla de precios actualizados (precios de mercado mayorista España, junio 2026, sin IVA) para nuestro ejemplo:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Precio unitario</th>
                    <th className="p-3 text-left font-medium">Fuente</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Lubina fresca entera', '18,00 €/kg', 'Mercabarna'],
                    ['Patata', '1,80 €/kg', 'Distribuidor'],
                    ['Pimiento verde', '2,50 €/kg', 'Mercabarna'],
                    ['Cebolla', '1,60 €/kg', 'Distribuidor'],
                    ['Tomate cherry', '4,50 €/kg', 'Mercabarna'],
                    ['Ajo', '8,00 €/kg', 'Distribuidor'],
                    ['AOVE', '9,50 €/L', 'Proveedor'],
                    ['Vino blanco (cocina)', '6,00 €/L', 'Distribuidor'],
                    ['Limón', '0,30 €/ud', 'Mercabarna'],
                    ['Perejil fresco', '12,00 €/kg', 'Mercabarna'],
                    ['Sal gruesa', '0,40 €/kg', 'Distribuidor'],
                    ['Pimienta negra', '25,00 €/kg', 'Proveedor'],
                  ].map(([ing, precio, fuente], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-blue-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500">{fuente}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800">Automatiza la actualización de precios</p>
                  <p className="text-blue-700 text-sm mt-1">
                    Con PLEKO, puedes <Link to="/ocr-facturas-restaurante" className="text-blue-800 underline hover:text-blue-900">escanear tus albaranes con el móvil</Link> y todos los precios se actualizan automáticamente en todas tus recetas. También puedes consultar <Link to="/precios-mercado" className="text-blue-800 underline hover:text-blue-900">precios de mercados mayoristas</Link> en tiempo real para comparar.
                  </p>
                </div>
              </div>
            </div>

            {/* ── PASO 4 ── */}
            <h2 id="paso-4" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">4</span>
                Aplica las mermas de cada ingrediente
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>merma</strong> es la cantidad de producto que se pierde al limpiarlo, pelarlo, despinarlo o procesarlo. Es el factor que más hosteleros olvidan al hacer escandallos, y el que más distorsiona los cálculos. Cuando compras 1,2 kg de lubina entera, no vas a servir 1,2 kg de carne: después de quitar la cabeza, las espinas, las vísceras y las escamas, te quedan solo <strong>660 gramos de carne aprovechable</strong>.
            </p>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-6">
              <p className="text-amber-800 font-semibold text-center mb-2">Fórmula de la merma</p>
              <p className="text-center text-xl font-bold text-amber-600 font-mono">
                Cantidad neta = Cantidad bruta × (1 - merma%)
              </p>
              <p className="text-center text-slate-600 mt-3 text-sm">
                Ejemplo: Lubina 1.200 g × (1 - 0,45) = 1.200 × 0,55 = <strong>660 g netos</strong>
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Veamos las mermas aplicadas a cada ingrediente de nuestra Lubina a la Espalda:
            </p>

            <div className="space-y-3 mb-6">
              {[
                { ing: 'Lubina entera', merma: '45%', motivo: 'Cabeza, espinas, vísceras, escamas', bruto: '1.200 g', neto: '660 g' },
                { ing: 'Patata', merma: '15%', motivo: 'Piel, ojos', bruto: '400 g', neto: '340 g' },
                { ing: 'Pimiento verde', merma: '15%', motivo: 'Semillas, tallo, nervios', bruto: '150 g', neto: '127 g' },
                { ing: 'Cebolla', merma: '10%', motivo: 'Piel seca, raíz', bruto: '200 g', neto: '180 g' },
                { ing: 'Tomate cherry', merma: '5%', motivo: 'Pedúnculo', bruto: '100 g', neto: '95 g' },
                { ing: 'Ajo', merma: '15%', motivo: 'Piel, tallo', bruto: '20 g', neto: '17 g' },
                { ing: 'Perejil', merma: '10%', motivo: 'Tallos gruesos', bruto: '10 g', neto: '9 g' },
              ].map(({ ing, merma, motivo, bruto, neto }) => (
                <div key={ing} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">{ing}</span>
                    <span className="text-amber-600 font-bold ml-2">{merma}</span>
                    <span className="text-slate-400 text-sm ml-2">({motivo})</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    {bruto} → <strong className="text-slate-700">{neto}</strong>
                  </div>
                </div>
              ))}
              <p className="text-sm text-slate-500 italic">
                AOVE, vino blanco, limón, sal y pimienta: merma 0% (se usan íntegramente).
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Las mermas no afectan al coste directo en este paso (pagas por la cantidad bruta), pero son esenciales para saber cuánto producto aprovechable obtienes realmente. Esto es clave cuando escalas recetas o comparas el <Link to="/mermas-cocina" className="text-blue-600 hover:underline">coste real por kilo utilizable</Link> entre proveedores.
            </p>

            {/* ── PASO 5 ── */}
            <h2 id="paso-5" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">5</span>
                Calcula el coste de cada ingrediente
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ahora viene el cálculo central del escandallo. Para cada ingrediente, multiplica la <strong>cantidad bruta de compra</strong> por el <strong>precio unitario</strong>. Recuerda que pagas por la cantidad bruta (la que compras), no por la neta (la que aprovechas).
            </p>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
              <p className="text-blue-800 font-semibold text-center mb-2">Fórmula del coste por ingrediente</p>
              <p className="text-center text-xl font-bold text-blue-600 font-mono">
                Coste = Cantidad bruta (en kg) × Precio (€/kg)
              </p>
              <p className="text-center text-slate-600 mt-3 text-sm">
                Ejemplo: Lubina 1,2 kg × 18,00 €/kg = <strong>21,60 €</strong>
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Aquí está el cálculo completo de cada ingrediente de nuestra Lubina a la Espalda:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cant. bruta</th>
                    <th className="p-3 text-right font-medium">Precio/ud</th>
                    <th className="p-3 text-center font-medium">Cálculo</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Lubina fresca entera', '1,2 kg', '18,00 €/kg', '1,2 × 18,00', '21,60 €'],
                    ['Patata panadera', '400 g', '1,80 €/kg', '0,4 × 1,80', '0,72 €'],
                    ['Pimiento verde', '150 g', '2,50 €/kg', '0,15 × 2,50', '0,38 €'],
                    ['Cebolla', '200 g', '1,60 €/kg', '0,2 × 1,60', '0,32 €'],
                    ['Tomate cherry', '100 g', '4,50 €/kg', '0,1 × 4,50', '0,45 €'],
                    ['Ajo (4 dientes)', '20 g', '8,00 €/kg', '0,02 × 8,00', '0,16 €'],
                    ['AOVE', '80 ml', '9,50 €/L', '0,08 × 9,50', '0,76 €'],
                    ['Vino blanco', '50 ml', '6,00 €/L', '0,05 × 6,00', '0,30 €'],
                    ['Limón', '1 ud', '0,30 €/ud', '1 × 0,30', '0,30 €'],
                    ['Perejil fresco', '10 g', '12,00 €/kg', '0,01 × 12,00', '0,12 €'],
                    ['Sal gruesa', '15 g', '0,40 €/kg', '0,015 × 0,40', '0,01 €'],
                    ['Pimienta negra', '2 g', '25,00 €/kg', '0,002 × 25,00', '0,05 €'],
                  ].map(([ing, cant, precio, calculo, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cant}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-400 text-xs font-mono">{calculo}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50">
                    <td colSpan={4} className="p-3 font-bold text-slate-800 text-lg">COSTE TOTAL DE LA RECETA</td>
                    <td className="p-3 text-right font-bold text-blue-600 text-lg">25,17 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              El coste total de los ingredientes para nuestra Lubina a la Espalda (4 raciones) es de <strong>25,17 €</strong>. Fíjate en cómo la lubina sola representa el <strong>85,8%</strong> del coste total — es un plato donde el ingrediente principal domina completamente el escandallo. En platos con muchos ingredientes de coste similar (como una paella), la distribución es más equilibrada.
            </p>

            {/* ── PASO 6 ── */}
            <h2 id="paso-6" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">6</span>
                Calcula el coste por ración y el food cost
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ya tienes el coste total. Ahora divídelo entre el número de raciones y compáralo con tu precio de venta para obtener el <strong>food cost</strong>, la métrica clave de rentabilidad:
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                <p className="text-blue-800 font-semibold mb-2">Coste por ración</p>
                <p className="text-center text-xl font-bold text-blue-600 font-mono">
                  25,17 € ÷ 4 raciones = <span className="text-2xl">6,29 €/ración</span>
                </p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-6">
                <p className="text-green-800 font-semibold mb-2">Food cost (%)</p>
                <p className="text-center text-xl font-bold text-green-600 font-mono">
                  (6,29 € ÷ 22,00 €) × 100 = <span className="text-2xl">28,6%</span>
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Con un PVP de 22 €, nuestra Lubina a la Espalda tiene un <strong>food cost del 28,6%</strong>. Evaluemos este resultado con el semáforo estándar de hostelería:
            </p>

            <div className="space-y-3 mb-6">
              {[
                { label: 'Verde — Excelente', range: 'Food cost < 30%', desc: 'Margen óptimo. El plato es rentable.', color: 'green', active: true },
                { label: 'Amarillo — Vigilar', range: 'Food cost 30–35%', desc: 'Margen justo. Busca optimizar.', color: 'yellow', active: false },
                { label: 'Rojo — Actuar', range: 'Food cost > 35%', desc: 'Margen insuficiente. Hay que intervenir.', color: 'red', active: false },
              ].map(({ label, range, desc, color, active }) => (
                <div
                  key={label}
                  className={`flex gap-4 p-4 rounded-xl border ${
                    active
                      ? `bg-${color}-50 border-${color}-300 ring-2 ring-${color}-200`
                      : 'bg-slate-50 border-slate-200 opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {color === 'green' && '🟢'} {color === 'yellow' && '🟡'} {color === 'red' && '🔴'} {label}{' '}
                      <span className="font-normal text-slate-500">({range})</span>
                    </p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                  {active && (
                    <span className="text-green-600 font-bold text-sm self-center">← Nuestra lubina (28,6%)</span>
                  )}
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-4 gap-3 mb-10">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">Coste total</p>
                <p className="text-2xl font-bold text-blue-600">25,17 €</p>
                <p className="text-xs text-slate-400">(4 raciones)</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">Coste / ración</p>
                <p className="text-2xl font-bold text-slate-800">6,29 €</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">Food cost</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  <p className="text-2xl font-bold text-green-600">28,6%</p>
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">Margen bruto</p>
                <p className="text-2xl font-bold text-emerald-600">15,71 €</p>
              </div>
            </div>

            {/* ── PASO 7 ── */}
            <h2 id="paso-7" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg">7</span>
                Ajusta precios o ingredientes según el resultado
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En nuestro ejemplo, la Lubina a la Espalda tiene un food cost del 28,6%, que está en zona verde. No necesita ajustes urgentes. Pero ¿qué harías si el food cost fuera superior al 35%? Tienes tres estrategias principales:
            </p>

            <div className="space-y-4 mb-6">
              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Estrategia 1: Subir el PVP</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es la opción más directa. Si tu lubina costara 8,50 €/ración en vez de 6,29 €, necesitarías un PVP de al menos 28,33 € para mantener un food cost del 30%. Evalúa si tu mercado y posicionamiento lo permiten. En un restaurante de nivel medio-alto en Barcelona o Madrid, un PVP de 24-26 € para una lubina a la espalda es perfectamente razonable.
                </p>
              </div>
              <div className="border-l-4 border-green-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Estrategia 2: Sustituir ingredientes caros</h3>
                <p className="text-slate-700 leading-relaxed">
                  Busca alternativas que mantengan la calidad del plato a menor coste. Por ejemplo, puedes usar una dorada (más barata que la lubina) o reducir la cantidad de AOVE y sustituir parte por aceite de girasol para la cocción. Cada céntimo cuenta cuando multiplicas por los platos servidos al día.
                </p>
              </div>
              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Estrategia 3: Ajustar gramaje o raciones</h3>
                <p className="text-slate-700 leading-relaxed">
                  Reducir ligeramente el gramaje por ración puede tener un impacto significativo. Pasar de 300 g a 270 g de lubina por persona (un 10% menos) reduce el coste del ingrediente principal sin que el cliente perciba una diferencia notable. También puedes compensar con más guarnición, que tiene un coste muy inferior.
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Si por el contrario tu food cost es muy bajo (por debajo del 20%), considera mejorar la calidad de los ingredientes. Un food cost demasiado bajo puede significar que estás usando materia prima de calidad inferior, lo que a largo plazo afecta a la percepción del cliente y a la reputación del restaurante.
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Para optimizar la carta completa, no solo platos individuales, te recomendamos nuestra guía de <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">ingeniería de menú</Link>, que explica cómo clasificar tus platos en Stars (rentables y populares), Plowhorses (populares pero con poco margen), Puzzles (rentables pero poco vendidos) y Dogs (ni rentables ni vendidos).
            </p>

            {/* ═══════════ SECTION 3: RESUMEN ═══════════ */}
            <h2 id="resumen" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Resumen del escandallo completo: Lubina a la Espalda
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aquí tienes el escandallo completo de nuestra Lubina a la Espalda en formato profesional. Esta es la ficha que deberías tener para cada plato de tu carta:
            </p>

            <div className="border-2 border-blue-200 rounded-2xl overflow-hidden mb-6">
              {/* Header */}
              <div className="bg-blue-500 text-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">Lubina a la Espalda</h3>
                    <p className="text-blue-100 text-sm mt-1">Escandallo completo — 4 raciones</p>
                  </div>
                  <ChefHat className="w-8 h-8 text-blue-200" />
                </div>
              </div>

              {/* Ingredient table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="p-3 text-left font-semibold text-slate-700">Ingrediente</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Cant. bruta</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Merma</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Cant. neta</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Precio/ud</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Coste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lubinaIngredients.map(({ nombre, cantBruta, merma, cantNeta, precioUd, coste }, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{nombre}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cantBruta}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-amber-600 font-medium">{merma}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cantNeta}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precioUd}</td>
                        <td className="p-3 border-b border-slate-100 text-right font-semibold text-slate-800">{coste}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50">
                      <td colSpan={5} className="p-3 font-bold text-slate-800 text-base">COSTE TOTAL (4 raciones)</td>
                      <td className="p-3 text-right font-bold text-blue-600 text-lg">25,17 €</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Metrics */}
              <div className="p-5 bg-slate-50 border-t border-slate-200">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Coste total</p>
                    <p className="text-lg font-bold text-blue-600">25,17 €</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Coste/ración</p>
                    <p className="text-lg font-bold text-slate-800">6,29 €</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-green-200">
                    <p className="text-xs text-slate-500">Food cost</p>
                    <p className="text-lg font-bold text-green-600">28,6%</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">PVP</p>
                    <p className="text-lg font-bold text-slate-800">22,00 €</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Margen bruto</p>
                    <p className="text-lg font-bold text-emerald-600">15,71 €</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Peso/ración</p>
                    <p className="text-lg font-bold text-slate-700">~394 g</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Este es exactamente el tipo de ficha que genera PLEKO automáticamente para cada receta. Con la ventaja de que, cuando sube el precio de la lubina, <strong>todos los escandallos que usan lubina se actualizan instantáneamente</strong>. <Link to="/calculadora-escandallo" className="text-blue-600 hover:underline">Prueba nuestra calculadora gratuita</Link> o <Link to="/register" className="text-blue-600 hover:underline">crea tu cuenta</Link> para gestionar toda tu carta.
            </p>

            {/* ═══════════ SECTION 4: CONSEJOS ═══════════ */}
            <h2 id="consejos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              5 consejos profesionales para escandallos perfectos
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Actualiza precios cada semana</h3>
                <p className="text-slate-700 leading-relaxed">
                  Los productos frescos pueden variar un 10-20% de una semana a otra, especialmente el pescado y las verduras de temporada. Un escandallo con precios de hace un mes ya no es fiable. Lo ideal es actualizar cada vez que recibes un albarán. Con PLEKO, escaneas el albarán con el móvil y se actualiza todo automáticamente.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. No olvides ingredientes pequeños (sal, aceite, especias)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Parece que cuestan poco, pero sumados representan entre un 5% y un 15% del coste total del plato. En nuestro ejemplo de lubina, el aceite de oliva solo ya supone 0,76 € (el 3% del total). En platos con frituras o salsas elaboradas, el aceite puede ser el segundo ingrediente más caro.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Revisa mermas con tu equipo de cocina</h3>
                <p className="text-slate-700 leading-relaxed">
                  Las tablas de merma genéricas son un punto de partida, pero la merma real depende de la habilidad de tu equipo, la calidad del producto y las técnicas de corte. Pesa el producto antes y después de procesarlo para calcular tu merma real. Un cocinero experimentado puede tener un 35% de merma en lubina donde un principiante tendría un 50%.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Escandalla TODA la carta, no solo los platos caros</h3>
                <p className="text-slate-700 leading-relaxed">
                  Muchos hosteleros solo hacen escandallo de los platos con ingredientes caros (mariscos, carnes premium). Pero los platos baratos con food cost descontrolado pueden costarte más a largo plazo porque se venden en mayor volumen. Una ensalada con un food cost del 45% servida 30 veces al día es peor que un solomillo con un 32% servido 5 veces.
                </p>
              </div>

              <div className="border-l-4 border-blue-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. Compara food cost entre platos para optimizar la carta</h3>
                <p className="text-slate-700 leading-relaxed">
                  Cuando tienes el escandallo de todos los platos, puedes comparar rentabilidades y tomar decisiones estratégicas: destacar platos rentables, reformular platos con margen bajo, eliminar "dogs" (platos que ni se venden ni son rentables). Esto se llama <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">ingeniería de menú</Link> y es una de las herramientas más potentes de gestión hostelera.
                </p>
              </div>
            </div>

            {/* ═══════════ SECTION 5: HERRAMIENTAS ═══════════ */}
            <h2 id="herramientas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Herramientas para hacer escandallos
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Hay tres formas principales de hacer escandallos. Cada una tiene sus ventajas según el tamaño de tu negocio:
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">📋</span> Excel / Papel
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Bueno para empezar y entender el proceso. Totalmente manual: cada cambio de precio requiere actualizar todas las hojas afectadas.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Sin curva de aprendizaje</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">✗</span>Actualización 100% manual</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">✗</span>Errores de fórmula frecuentes</li>
                </ul>
                <Link to="/plantilla-escandallo-excel" className="text-sm text-blue-600 hover:underline block mt-3">
                  Descargar plantilla Excel gratis →
                </Link>
              </div>

              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">🌐</span> Calculadora online
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Rápida para cálculos puntuales. No guarda historial ni permite gestionar toda la carta de forma integrada.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis y sin registro</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Cálculo instantáneo</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">✗</span>No guarda datos</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">✗</span>Sin sub-recetas</li>
                </ul>
                <Link to="/calculadora-escandallo" className="text-sm text-blue-600 hover:underline block mt-3">
                  Usar calculadora gratuita →
                </Link>
              </div>

              <div className="border-2 border-blue-300 bg-blue-50 rounded-xl p-5">
                <h3 className="font-bold text-blue-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">⚡</span> Software profesional
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  Automático, con OCR de facturas, mermas inteligentes, sub-recetas y alertas de food cost. La opción para gestionar un restaurante real.
                </p>
                <ul className="space-y-1 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Precios se actualizan solos</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />OCR de albaranes</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Sub-recetas en cascada</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Multi-usuario</li>
                </ul>
                <Link to="/software-escandallo" className="text-sm text-blue-700 font-semibold hover:underline block mt-3">
                  Ver PLEKO — software de escandallos →
                </Link>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Si tienes menos de 10 recetas y acabas de empezar, una hoja Excel o nuestra <Link to="/calculadora-escandallo" className="text-blue-600 hover:underline">calculadora online gratuita</Link> son perfectas para aprender. Pero si gestionas un restaurante con 30+ platos, necesitas un sistema que actualice todos los escandallos automáticamente cuando cambia un precio. La diferencia entre actualizar manualmente 50 hojas de Excel y que un software lo haga por ti son <strong>horas de trabajo cada semana</strong>.
            </p>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre cómo hacer escandallos
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guías relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Qué es un escandallo: guía completa' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cócteles' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online (gratis)' },
                { to: '/software-escandallo', label: 'El mejor software de escandallos' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: qué es y cómo calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Ya sabes cómo hacer un escandallo. Ahora hazlo en 2 minutos.
            </h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automáticamente el coste de todas tus recetas, controla mermas, escanea facturas con IA y te avisa cuando el food cost se dispara. Prueba gratis 14 días.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-blue-200 text-sm">Sin tarjeta de crédito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
